<template>
  <c-card>
    <c-card-header>
      <c-icon name="cil-notes" />
      Form Validation
      <a class="badge badge-danger" href="https://coreui.io/pro/vue/">CoreUI Pro</a>
      <div class="card-header-actions">
        <a
          class="card-header-action"
          href="https://github.com/vuelidate/vuelidate"
          target="_blank"
          rel="noreferrer noopener"
        >
          <small class="text-muted">docs</small>
        </a>
      </div>
    </c-card-header>
    <c-card-body>
      <c-link href="https://github.com/vuelidate/vuelidate" target="_blank" rel="noreferrer noopener">
        Vuelidate
      </c-link>
      provides
      <cite>Simple, lightweight model-based validation for Vue.js.</cite>
      In this view Vuelidate features are integrated with CoreUI Vue form components.
      <hr />
      <c-row>
        <c-col lg="6">
          <!--<h6>Simple Form</h6>-->
          <c-form>
            <c-input
              label="First Name"
              :lazy="false"
              :value.sync="$v.form.firstName.$model"
              :is-valid="checkIfValid('firstName')"
              placeholder="First Name"
              autocomplete="given-name"
              invalid-feedback="This is a required field and must be at least 2 characters"
            />

            <c-input
              label="Last Name"
              :lazy="false"
              :value.sync="$v.form.lastName.$model"
              :is-valid="checkIfValid('lastName')"
              placeholder="Last Name"
              autocomplete="family-name"
              invalid-feedback="This is a required field and must be at least 1 character"
            />

            <c-input
              label="User Name"
              :lazy="false"
              :value.sync="$v.form.userName.$model"
              :is-valid="checkIfValid('userName')"
              placeholder="User Name"
              autocomplete="username"
              invalid-feedback="This is a required field and must be at least 5 character"
            />

            <c-input
              label="Email"
              type="email"
              :lazy="false"
              :value.sync="$v.form.email.$model"
              :is-valid="checkIfValid('email')"
              placeholder="Email"
              autocomplete="email"
              invalid-feedback="This is a required field and must be valid e-mail address"
            />

            <c-row>
              <c-col md="6">
                <c-input
                  :is-valid="checkIfValid('password')"
                  :value.sync="$v.form.password.$model"
                  label="Password"
                  type="password"
                  placeholder="Password"
                  autocomplete="new-password"
                  invalid-feedback="Required password containing at least: number, uppercase and lowercase letter, 8 characters"
                />
              </c-col>
              <c-col md="6">
                <c-input
                  :is-valid="checkIfValid('password')"
                  :value.sync="$v.form.confirmPassword.$model"
                  label="Confirm Password"
                  type="password"
                  placeholder="Password"
                  autocomplete="new-password"
                  invalid-feedback="Passwords must match"
                />
              </c-col>
            </c-row>
            <c-input-checkbox
              :is-valid="checkIfValid('accept')"
              :checked.sync="$v.form.accept.$model"
              label="I accept the terms of use"
              invalid-feedback="You must accept before submitting"
              custom
              class="mb-4"
            />
            <c-button color="primary" :disabled="!isValid || submitted" @click="submit">
              Submit
            </c-button>
            <c-button class="ml-1" color="success" :disabled="isValid" @click="validate">
              Validate
            </c-button>
            <c-button class="ml-1" color="danger" :disabled="!isDirty" @click="reset">
              Reset
            </c-button>
          </c-form>
          <br />
        </c-col>

        <c-col lg="6">
          <c-card :class="`bg-${submitted ? 'info' : 'secondary'}`">
            <pre>{{ formString }}</pre>
          </c-card>
        </c-col>
      </c-row>
    </c-card-body>
  </c-card>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {
  required, minLength, email, sameAs, helpers,
} from 'vuelidate/lib/validators';

export default {
  name: 'ValidationForms',
  mixins: [validationMixin],
  data() {
    return {
      form: this.getEmptyForm(),
      submitted: false,
    };
  },
  computed: {
    formString() {
      return JSON.stringify(this.form, null, 4);
    },
    isValid() {
      return !this.$v.form.$invalid;
    },
    isDirty() {
      return this.$v.form.$anyDirty;
    },
  },
  validations: {
    form: {
      firstName: {
        required,
        minLength: minLength(3),
      },
      lastName: {
        required,
        minLength: minLength(2),
      },
      userName: {
        required,
        minLength: minLength(5),
      },
      email: {
        required,
        email,
      },
      password: {
        required,
        minLength: minLength(8),
        strongPass: helpers.regex('strongPass', /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/),
      },
      confirmPassword: {
        required,
        sameAsPassword: sameAs('password'),
      },
      accept: {
        required,
        mustAccept: (val) => val,
      },
    },
  },
  methods: {
    checkIfValid(fieldName) {
      const field = this.$v.form[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === '');
    },

    submit() {
      if (this.isValid) {
        this.submitted = true;
      }
    },

    validate() {
      this.$v.$touch();
    },

    reset() {
      this.form = this.getEmptyForm();
      this.submitted = false;
      this.$v.$reset();
    },

    getEmptyForm() {
      return {
        firstName: '',
        lastName: '',
        userName: '',
        email: '',
        password: '',
        confirmPassword: '',
        accept: false,
      };
    },
  },
};
</script>
